import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { BadgeProvider, useBadges } from '../../hooks/useBadges';
import { Badge } from '../../components/ui/badge';
import { 
  ShoppingCart, 
  Package,
  ClipboardList,
  Building2,
  MessageSquare,
  Briefcase,
  FileText,
  Wrench,
  HardHat,
  PackageCheck,
  Warehouse,
  Calendar,
  AlertTriangle
} from 'lucide-react';

// Satın Alma Paneli bileşenlerini import et - aynı görünüm için
import PurchasingStock from '../purchasing/PurchasingStock';
import PurchasingDemirbaslar from '../purchasing/PurchasingDemirbaslar';
import PurchasingGlobalData from '../purchasing/PurchasingGlobalData';
import PurchasingCommunication from '../purchasing/PurchasingCommunication';
import PurchasingJobs from '../purchasing/PurchasingJobs';
import PurchasingOffers from '../purchasing/PurchasingOffers';
import PurchasingServices from '../purchasing/PurchasingServices';
import PurchasingSubcontractors from '../purchasing/PurchasingSubcontractors';
import PurchasingPurchased from '../purchasing/PurchasingPurchased';
import PurchasingWarehouses from '../purchasing/PurchasingWarehouses';
import PurchasingWarnings from '../purchasing/PurchasingWarnings';
import WorkCalendar from '../work/WorkCalendar';

const API = process.env.REACT_APP_BACKEND_URL;

const BoardBirimSatinAlmaContent = () => {
  const { user } = useAuth();
  const { getBadgeCount: getNewBadgeCount, markSeen } = useBadges();
  const [activeMenu, setActiveMenu] = useState('stock');
  const [unreadCounts, setUnreadCounts] = useState({});
  const [jobsStats, setJobsStats] = useState(null);
  const [unreadPurchasedCount, setUnreadPurchasedCount] = useState(0);

  const fetchUnreadCounts = useCallback(async () => {
    try {
      const token = localStorage.getItem('fortis_token');
      
      // Genel bildirim sayılarını al
      const response = await fetch(`${API}/api/notifications/unread-counts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCounts(data);
      }
      
      // İş istatistiklerini al
      const jobsResponse = await fetch(`${API}/api/purchasing/jobs/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (jobsResponse.ok) {
        const data = await jobsResponse.json();
        setJobsStats(data);
      }
      
      // Alınan ürünler okunmamış sayısını al
      const purchasedResponse = await fetch(`${API}/api/purchasing/orders/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (purchasedResponse.ok) {
        const data = await purchasedResponse.json();
        setUnreadPurchasedCount(data.count || 0);
      }
    } catch (error) {
      console.error('Bildirimler alınamadı:', error);
    }
  }, []);

  useEffect(() => {
    fetchUnreadCounts();
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCounts]);

  // Menu Items - PurchasingPanel ile AYNI
  const menuItems = [
    { key: 'stock', icon: Package, label: 'Stok Özeti', menuKey: 'stock', badgeKey: null },
    { key: 'demirbaslar', icon: ClipboardList, label: 'Demirbaşlar', menuKey: 'demirbaslar', badgeKey: null },
    { key: 'global-data', icon: Building2, label: 'Cari & Stok Kartı', menuKey: 'global_data', badgeKey: null },
    { key: 'communication', icon: MessageSquare, label: 'İletişim', menuKey: 'communication', badgeKey: 'purchasing_messages' },
    { key: 'jobs', icon: Briefcase, label: 'İşler', menuKey: 'jobs', badgeKey: 'purchasing_inbox', useUnreadJobs: true },
    { key: 'offers', icon: FileText, label: 'Teklifler', menuKey: 'offers', badgeKey: null },
    { key: 'services', icon: Wrench, label: 'Alınan Hizmetler', menuKey: 'services', badgeKey: null },
    { key: 'subcontractors', icon: HardHat, label: 'Taşeronlar', menuKey: 'subcontractors', badgeKey: null },
    { key: 'purchased', icon: PackageCheck, label: 'Alınan Ürünler', menuKey: 'purchased', badgeKey: null, useUnreadPurchased: true },
    { key: 'warehouses', icon: Warehouse, label: 'Depolar', menuKey: 'warehouses', badgeKey: null },
    { key: 'calendar', icon: Calendar, label: 'Takvim', menuKey: 'calendar', badgeKey: null },
  ];

  // Badge sayısını hesapla
  const getBadgeCount = (item) => {
    let count = getNewBadgeCount(item.menuKey);
    if (count > 0) return count;
    
    count = 0;
    if (item.useUnreadJobs && jobsStats) {
      count += jobsStats.unread || 0;
    }
    if (item.useUnreadPurchased) {
      count += unreadPurchasedCount;
    }
    if (item.badgeKey && unreadCounts[item.badgeKey]) {
      count += unreadCounts[item.badgeKey];
    }
    return count;
  };

  // İçerik render - Satın Alma Paneli bileşenlerini doğrudan kullan
  const renderContent = () => {
    switch (activeMenu) {
      case 'stock':
        return <PurchasingStock readOnly={true} />;
      case 'demirbaslar':
        return <PurchasingDemirbaslar readOnly={true} />;
      case 'global-data':
        return <PurchasingGlobalData readOnly={true} />;
      case 'communication':
        return <PurchasingCommunication readOnly={true} />;
      case 'jobs':
        return <PurchasingJobs isBoard={true} readOnly={true} />;
      case 'offers':
        return <PurchasingOffers readOnly={true} />;
      case 'services':
        return <PurchasingServices viewMode="board" readOnly={true} />;
      case 'subcontractors':
        return <PurchasingSubcontractors viewMode="board" readOnly={true} />;
      case 'purchased':
        return <PurchasingPurchased readOnly={true} />;
      case 'warehouses':
        return <PurchasingWarehouses readOnly={true} />;
      case 'calendar':
        return <WorkCalendar readOnly={true} />;
      case 'warnings':
        return <PurchasingWarnings readOnly={true} />;
      default:
        return <PurchasingStock readOnly={true} />;
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="board-birim-satin-alma">
      {/* Header - PurchasingPanel ile aynı stil */}
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--border))] bg-white/70 backdrop-blur">
        <div className="container mx-auto flex h-16 items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-8 w-8 text-orange-600" />
              <span className="text-xl font-bold text-orange-600">Satın Alma</span>
            </div>
            <Badge variant="outline" className="ml-2 text-xs bg-orange-50 text-orange-700 border-orange-300">Salt Okunur</Badge>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-600">{user?.name}</span>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar - PurchasingPanel ile AYNI */}
        <aside className="w-64 min-h-[calc(100vh-4rem)] border-r border-[hsl(var(--border))] bg-white p-4">
          <nav className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const badgeCount = getBadgeCount(item);
              
              return (
                <button
                  key={item.key}
                  onClick={() => {
                    setActiveMenu(item.key);
                    if (item.menuKey) markSeen(item.menuKey);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                    activeMenu === item.key
                      ? 'bg-orange-100 text-orange-700'
                      : 'hover:bg-gray-100 text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="font-medium">{item.label}</span>
                  {badgeCount > 0 && (
                    <Badge className={`ml-auto ${
                      activeMenu === item.key ? 'bg-orange-500 text-white' : 'bg-red-500 text-white'
                    }`}>
                      {badgeCount}
                    </Badge>
                  )}
                </button>
              );
            })}

            {/* Uyarılar butonu - menü listesinin en altında (Takvim'in hemen altında) */}
            <button
              onClick={() => setActiveMenu('warnings')}
              data-testid="menu-warnings"
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors ${
                activeMenu === 'warnings'
                  ? 'bg-amber-100 text-amber-700'
                  : 'hover:bg-amber-50 text-gray-600 hover:text-amber-700'
              }`}
            >
              <AlertTriangle className="h-5 w-5" />
              <span className="font-medium">Uyarılar</span>
            </button>
          </nav>
        </aside>

        {/* Main Content - Satın Alma Paneli bileşenlerini göster */}
        <main className="flex-1 p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
};

const BoardBirimSatinAlma = () => {
  return (
    <BadgeProvider panel="purchasing">
      <BoardBirimSatinAlmaContent />
    </BadgeProvider>
  );
};

export default BoardBirimSatinAlma;
